import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  UserPlus, MapPin, GraduationCap, BadgeCheck, IdCard, Check,
  Loader2, ArrowRight, ArrowLeft, Sparkles,
} from "lucide-react";

const STEPS = ["Account", "About You", "Location", "Academics", "Your ID"];

export default function OpenOnboardWizard() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Step 0 — Account
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Step 1 — Demographics
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [gender, setGender] = useState("");

  // Step 2 — Geo cascade
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [district, setDistrict] = useState("");
  const [city, setCity] = useState("");
  const [postal, setPostal] = useState("");

  // Step 3 — Academic taxonomy
  const [boards, setBoards] = useState([]);
  const [grades, setGrades] = useState([]);
  const [plan, setPlan] = useState(null);
  const [board, setBoard] = useState("");
  const [grade, setGrade] = useState("");

  const countryName = useMemo(
    () => countries.find(c => c.country_code === country)?.country_name || country,
    [countries, country]
  );

  useEffect(() => {
    api.get("/geo/countries").then(r => setCountries(r.data)).catch(() => {});
    api.get("/open-school/boards").then(r => setBoards(r.data)).catch(() => {});
    api.get("/open-school/grades").then(r => setGrades(r.data)).catch(() => {});
    api.get("/membership/plans/open_student").then(r => setPlan(r.data[0] || null)).catch(() => {});
  }, []);

  useEffect(() => {
    setState(""); setDistrict(""); setCity(""); setStates([]); setDistricts([]); setCities([]);
    if (country) api.get("/geo/states", { params: { country_code: country } }).then(r => setStates(r.data));
  }, [country]);
  useEffect(() => {
    setDistrict(""); setCity(""); setDistricts([]); setCities([]);
    if (country && state) api.get("/geo/districts", { params: { country_code: country, state } }).then(r => setDistricts(r.data));
  }, [state]); // eslint-disable-line
  useEffect(() => {
    setCity(""); setCities([]);
    if (country && state && district) api.get("/geo/cities", { params: { country_code: country, state, district } }).then(r => setCities(r.data));
  }, [district]); // eslint-disable-line

  const canNext = useMemo(() => {
    if (step === 0) return name.trim() && /\S+@\S+\.\S+/.test(email) && password.length >= 6;
    if (step === 1) return phone.trim().length >= 6;
    if (step === 2) return country && state && district && city && postal.trim();
    if (step === 3) return board && grade;
    return true;
  }, [step, name, email, password, phone, country, state, district, city, postal, board, grade]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post("/open-school/onboard", {
        name, email, password, phone, dob: dob || null, gender: gender || null,
        country_code: country, country_name: countryName,
        state, district, city, postal_code: postal,
        board_code: board, grade,
      });
      setUser(r.data.user);
      setDone(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign-up failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#0B1120] grid place-items-center p-6">
        <div className="nb-card bg-[#86EFAC] p-8 max-w-lg w-full space-y-5 text-center" data-testid="open-onboard-success">
          <div className="w-16 h-16 rounded-full bg-white border-2 border-black grid place-items-center mx-auto">
            <Sparkles size={30} />
          </div>
          <h1 className="display text-3xl font-black">You're in — welcome to Ponderr!</h1>
          <p className="text-sm">Your <b>90-day free trial</b> is active until <b>{(done.trial_expires_at || "").slice(0, 10)}</b>.</p>
          <div className="nb-card bg-white p-5 border-2 border-black">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 font-bold">Your Open School ID</div>
            <div className="display text-2xl md:text-3xl font-black mono tracking-tight mt-1" data-testid="open-onboard-id">
              {done.open_student_id}
            </div>
          </div>
          <button className="nb-btn w-full justify-center" onClick={() => nav("/open")} data-testid="open-onboard-goto-console">
            Enter My Console <ArrowRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  const StepIcon = [UserPlus, BadgeCheck, MapPin, GraduationCap, IdCard][step];

  return (
    <div className="min-h-screen bg-[#0B1120] text-white p-4 md:p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-[#C4B5FD] border-2 border-black grid place-items-center shadow-[3px_3px_0_0_#000]">
              <GraduationCap size={22} className="text-black" />
            </div>
            <h1 className="display text-2xl md:text-3xl font-black">Join Ponderr Open School</h1>
          </div>
          <Link to="/login" className="text-xs md:text-sm underline decoration-2 underline-offset-4 opacity-80" data-testid="open-onboard-login-link">
            Have an account? Sign in →
          </Link>
        </div>

        {/* Stepper */}
        <div className="grid grid-cols-5 gap-2">
          {STEPS.map((s, i) => (
            <div key={s}
              className={`nb-card-sm p-2 text-center text-[10px] md:text-xs font-bold border-2 border-black transition-colors
                ${i === step ? "bg-[#FDE047] text-black" : i < step ? "bg-[#86EFAC] text-black" : "bg-[#334155] text-white/80"}`}
              data-testid={`open-onboard-step-${i}`}>
              {i + 1}. {s}
            </div>
          ))}
        </div>

        <div className="nb-card bg-white text-black p-6 space-y-4 border-2 border-black shadow-[6px_6px_0_0_#C4B5FD]">
          <div className="flex items-center gap-2">
            <StepIcon size={20} />
            <h2 className="display text-xl font-bold">{STEPS[step]}</h2>
            <span className="ml-auto nb-badge bg-[#93C5FD] text-xs">Step {step + 1} / 5</span>
          </div>

          {step === 0 && (
            <div className="space-y-3" data-testid="open-onboard-panel-account">
              <button type="button" disabled
                className="nb-btn nb-btn-secondary w-full justify-center opacity-60 cursor-not-allowed"
                title="Google sign-in will be enabled once OAuth keys are configured by the platform."
                data-testid="open-onboard-google-btn">
                <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.4 5.4 2.5 13.3l7.9 6.1C12.3 13.2 17.6 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.4 5.7C43.9 37.6 46.5 31.6 46.5 24.5z"/><path fill="#FBBC05" d="M10.4 28.6c-.5-1.4-.8-3-.8-4.6s.3-3.2.8-4.6l-7.9-6.1C.9 16.5 0 20.1 0 24s.9 7.5 2.5 10.7l7.9-6.1z"/><path fill="#34A853" d="M24 48c6.3 0 11.6-2.1 15.5-5.6l-7.4-5.7c-2.1 1.4-4.8 2.3-8.1 2.3-6.4 0-11.7-3.7-13.6-9.9l-7.9 6.1C6.4 42.6 14.6 48 24 48z"/></svg>
                Continue with Google (coming soon)
              </button>
              <div className="text-center text-xs text-slate-500">or sign up with email</div>
              <input className="nb-input" placeholder="Full Name *" value={name} onChange={e => setName(e.target.value)} data-testid="open-onboard-name" />
              <input className="nb-input" placeholder="Email *" value={email} onChange={e => setEmail(e.target.value)} data-testid="open-onboard-email" />
              <input type="password" className="nb-input" placeholder="Password (min 6 chars) *" value={password} onChange={e => setPassword(e.target.value)} data-testid="open-onboard-password" />
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3" data-testid="open-onboard-panel-demographics">
              <input className="nb-input mono" placeholder="Mobile / Phone *" value={phone} onChange={e => setPhone(e.target.value)} data-testid="open-onboard-phone" />
              <div className="grid md:grid-cols-2 gap-3">
                <label className="block">
                  <span className="nb-label text-[11px]">Date of Birth</span>
                  <input type="date" className="nb-input mt-1" value={dob} onChange={e => setDob(e.target.value)} data-testid="open-onboard-dob" />
                </label>
                <label className="block">
                  <span className="nb-label text-[11px]">Gender</span>
                  <select className="nb-select mt-1" value={gender} onChange={e => setGender(e.target.value)} data-testid="open-onboard-gender">
                    <option value="">Prefer not to say</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </label>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3" data-testid="open-onboard-panel-location">
              <div className="grid md:grid-cols-2 gap-3">
                <select className="nb-select" value={country} onChange={e => setCountry(e.target.value)} data-testid="open-onboard-country">
                  <option value="">Country…</option>
                  {countries.map(c => <option key={c.country_code} value={c.country_code}>{c.country_name}</option>)}
                </select>
                <select className="nb-select" value={state} onChange={e => setState(e.target.value)} disabled={!country} data-testid="open-onboard-state">
                  <option value="">State…</option>
                  {states.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select className="nb-select" value={district} onChange={e => setDistrict(e.target.value)} disabled={!state} data-testid="open-onboard-district">
                  <option value="">District…</option>
                  {districts.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                {state && districts.length === 0 &&
                  <p className="text-xs text-amber-700 md:col-span-2" data-testid="open-onboard-nodistricts">No districts added yet — add via Global Admin Territories.</p>}
                <select className="nb-select" value={city} onChange={e => setCity(e.target.value)} disabled={!district} data-testid="open-onboard-city">
                  <option value="">City…</option>
                  {cities.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <input className="nb-input mono" placeholder="Postal / ZIP / Pincode *" value={postal} onChange={e => setPostal(e.target.value)} data-testid="open-onboard-postal" />
              <p className="text-xs text-slate-500">Open School is available everywhere — your location personalises content and events.</p>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3" data-testid="open-onboard-panel-academics">
              <label className="block">
                <span className="nb-label text-[11px]">Academic Board *</span>
                <select className="nb-select mt-1" value={board} onChange={e => setBoard(e.target.value)} data-testid="open-onboard-board">
                  <option value="">Select board…</option>
                  {boards.map(b => <option key={b.code} value={b.code}>{b.name} ({b.code})</option>)}
                </select>
              </label>
              <label className="block">
                <span className="nb-label text-[11px]">Grade / Class *</span>
                <select className="nb-select mt-1" value={grade} onChange={e => setGrade(e.target.value)} data-testid="open-onboard-grade">
                  <option value="">Select grade…</option>
                  {grades.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                </select>
              </label>
              <p className="text-xs text-slate-500">Your Ponderr Learn, LMS curriculum and assessments map to this board &amp; grade.</p>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-3" data-testid="open-onboard-panel-confirm">
              <div className="nb-card-sm p-4 bg-[#FAF7F0] border-2 border-black text-sm space-y-1">
                <div><b>{name}</b> · {email}</div>
                <div>{grade} · {boards.find(b => b.code === board)?.name || board}</div>
                <div>{city}, {state}, {countryName} · {postal}</div>
                <div>{phone}</div>
              </div>
              <div className="nb-card-sm p-4 bg-[#DDD6FE] border-2 border-black text-sm">
                On confirm we'll generate your unique <b>Ponderr Open School ID</b> and start your
                <b> 90-day free trial</b> — no card required.
              </div>
              {plan && (
                <div className="nb-card-sm p-4 bg-white border-2 border-black" data-testid="open-onboard-plan">
                  <div className="text-[11px] uppercase font-bold tracking-wide text-slate-500">Your Plan</div>
                  <div className="display text-lg font-black">{plan.name} · ₹{Number(plan.effective_price ?? plan.base_price).toLocaleString()}/{plan.billing_interval === "monthly" ? "mo" : "yr"}</div>
                  {plan.trial_days > 0 && <span className="nb-badge bg-[#FDE047] text-[10px]">{plan.trial_days}-day free trial</span>}
                  <ul className="text-xs mt-2 space-y-0.5">{(plan.features || []).map((x, i) => <li key={i}>✓ {x}</li>)}</ul>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-between pt-2">
            <button className="nb-btn nb-btn-ghost" disabled={step === 0} onClick={() => setStep(s => s - 1)} data-testid="open-onboard-back">
              <ArrowLeft size={14} /> Back
            </button>
            {step < 4
              ? <button className="nb-btn" disabled={!canNext} onClick={() => setStep(s => s + 1)} data-testid="open-onboard-next">
                  Next <ArrowRight size={14} />
                </button>
              : <button className="nb-btn" disabled={submitting} onClick={submit} data-testid="open-onboard-submit">
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <><Check size={16} /> Generate My ID &amp; Start Trial</>}
                </button>}
          </div>
        </div>
      </div>
    </div>
  );
}
